import type { Location, StoreLocations } from "@hobbyshopify/backend-client";
import { ChangeNotifier } from "subtree.js/core";
import type { LoadStatus } from "../primitives/load-status";

export const isMappable = (location: Location) => location.latitude !== null && location.longitude !== null;

/**
 * How long the server's immediate geocode attempt is given before we stop
 * watching. It normally lands in a second or two; past this the address either
 * did not resolve or the attempt was lost, and the backend's sweep owns the retry.
 */
const GEOCODE_GRACE_MS = 2 * 60 * 1000;

export const isAwaitingGeocode = (location: Location) =>
  !isMappable(location) && Date.now() - location.createdAt.getTime() < GEOCODE_GRACE_MS;

/** Whether this branch can take a card in person yet. */
export const takesInPersonPayments = (location: Location) => location.stripeTerminalLocationId !== null;

/**
 * The selected shop's branches and its ship-from choice, one request, one
 * cache: two fetches of the same endpoint would be two moments in time on one
 * screen. Keyed by the shop, so a selection change empties it.
 */
export class LocationsRepository extends ChangeNotifier {
  #storeId: string | null = null;
  #status: LoadStatus = { kind: "idle" };
  #data: StoreLocations | null = null;
  #stale = false;

  /** The shop the data belongs to; null before the first load. */
  get storeId(): string | null {
    return this.#storeId;
  }

  get status(): LoadStatus {
    return this.#status;
  }

  get locations(): readonly Location[] {
    return this.#data?.locations ?? [];
  }

  get shipFrom(): StoreLocations["shipFrom"] | null {
    return this.#data?.shipFrom ?? null;
  }

  get isStale(): boolean {
    return this.#stale;
  }

  /** Loaded for this exact shop and not marked stale. */
  isFreshFor(storeId: string): boolean {
    return this.#storeId === storeId && this.#status.kind === "loaded" && !this.#stale;
  }

  /**
   * Loaded (or failed) for this exact shop: the gate can decide. A refetch of a
   * list already on screen still counts, so a save never swaps the page for a
   * spinner and loses the form.
   */
  isSettledFor(storeId: string): boolean {
    if (this.#storeId !== storeId) return false;
    if (this.#status.kind === "loaded" || this.#status.kind === "error") return true;
    return this.#status.kind === "loading" && this.#data !== null;
  }

  /** A location was created without coordinates a moment ago and may gain them any second. */
  get awaitingGeocode(): boolean {
    return this.locations.some(isAwaitingGeocode);
  }

  setLoading(storeId: string): void {
    if (this.#storeId !== storeId) this.#data = null;
    this.#storeId = storeId;
    this.#stale = false;
    this.#set({ kind: "loading" });
  }

  setLoaded(storeId: string, data: StoreLocations): void {
    this.#storeId = storeId;
    this.#data = data;
    this.#set({ kind: "loaded" });
  }

  setError(storeId: string, message: string): void {
    this.#storeId = storeId;
    this.#set({ kind: "error", message });
  }

  /** A branch was added or edited: whoever shows the list should fetch again. */
  markStale(): void {
    if (this.#stale) return;
    this.#stale = true;
    this.notifyListeners();
  }

  reset(): void {
    this.#storeId = null;
    this.#data = null;
    this.#stale = false;
    this.#set({ kind: "idle" });
  }

  #set(status: LoadStatus): void {
    this.#status = status;
    this.notifyListeners();
  }
}
